/**
 * User authentication hook for managing user state and authentication.
 * Integrates with ALB OIDC authentication via the backend /me endpoint.
 * Stage-agnostic - works with any environment through relative URLs.
 */

import { useState, useEffect } from 'react';
import { api, UserInfo } from '../services/api';
import { preserveQueryParams } from './useQueryParams';

export const useUser = () => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userData = await api.getMe();
      
      if (userData?.authenticated) {
        setUser(userData);
        console.log(`User authenticated: ${userData.firstName || ''} ${userData.lastName || ''} (${userData.username || userData.email})`);
      } else {
        setUser(null);
        console.log('User not authenticated - login required');
      }
    } catch (err) {
      console.error('Authentication check failed:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    console.log('Initiating login flow...');
    
    // Preserve query params before redirect
    preserveQueryParams();
    
    // Trigger authentication by navigating to the dedicated auth endpoint under /api
    // ALB will handle the redirect to Cognito automatically
    // Using relative URL makes this stage-agnostic
    window.location.href = '/api/auth/login';
  };

  const logout = () => {
    console.log('Initiating logout flow...');
    // Navigate to the logout endpoint which will clear the session
    // Using relative URL makes this stage-agnostic
    window.location.href = '/api/auth/logout';
  };

  return { 
    user, 
    loading, 
    error, 
    login,
    logout, 
    checkAuthentication,
    isAuthenticated: user?.authenticated || false
  };
};
